import ProductCard from '@/components/common/ProductCard';
import { useProductBySlug, useRelatedProducts } from '@/hooks/useProducts';
import { isBook } from '@bookstore/shared';
import { useCart } from '@/hooks/useCart';
import { useWishlist } from '@/hooks/useWishlist';
import {
  formatPrice,
  calculateDiscountPercentage,
  ProductType,
} from '@bookstore/shared';
import { getFirebaseAuth } from '@bookstore/shared/browser';
import {
  Heart,
  ShoppingCart,
  ChevronLeft,
  Minus,
  Plus,
  Check,
  Package,
  Truck,
  Shield,
  Edit,
  MessageSquare,
  Cross,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import ReviewForm from '@/components/common/ReviewForm';
import { format } from 'date-fns';
import { useProductReviews, useUserReview } from '@/hooks/useReviews';
import { Modal } from '@/components/common/Modal';
import Pagination from '@/components/common/Pagination';
import Placeholder from '@/assets/placeholder.webp';

const ProductDetailPage = () => {
  const { productSlug } = useParams();
  const navigate = useNavigate();
  const { data: product, isLoading } = useProductBySlug(productSlug);
  const [loadRelated, setLoadRelated] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const auth = getFirebaseAuth();
  const currentUserId = auth.currentUser?.uid;
  const [imageLoaded, setImageLoaded] = useState(false);
  const { data: reviews = [] as any[], isLoading: reviewsLoading } = useProductReviews(
    product?.id
  );
  const { data: userReview } = useUserReview(product?.id, currentUserId);

  useEffect(() => {
    const t = setTimeout(() => setLoadRelated(true), 700);
    return () => clearTimeout(t);
  }, []);

  // scroll to top when product changes
  useEffect(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [product?.id]);

  const { data: relatedProducts } = useRelatedProducts(
    loadRelated ? product?.id : undefined,
    loadRelated ? product?.categoryId : undefined,
    loadRelated && product ? product.productType : undefined,
    4
  );

  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);

  const { addToCart, isInCart, getItemQuantity } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();

  const inWishlist = product ? isInWishlist(product.id) : false;
  const inCart = product ? isInCart(product.id) : false;
  const cartQuantity = product ? getItemQuantity(product.id) : 0;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container-custom py-20">
          <div className="animate-pulse space-y-10">
            <div className="h-4 w-48 bg-primary-200 rounded" />
            <div className="grid lg:grid-cols-2 gap-20">
              <div className="space-y-6">
                <div className="aspect-[3/4] rounded-2xl bg-primary-200" />
                <div className="grid grid-cols-4 gap-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="aspect-[3/4] rounded-xl bg-primary-200" />
                  ))}
                </div>
              </div>
              <div className="space-y-6">
                <div className="h-10 bg-primary-200 rounded w-3/4" />
                <div className="h-6 bg-primary-200 rounded w-1/2" />
                <div className="h-8 bg-primary-200 rounded w-1/3" />
                <div className="space-y-3">
                  <div className="h-4 bg-primary-200 rounded" />
                  <div className="h-4 bg-primary-200 rounded w-5/6" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md">
          <div className="w-24 h-24 bg-background-soft rounded-full flex items-center justify-center mx-auto mb-6">
            <Package className="w-10 h-10 text-primary-600" />
          </div>
          <h2 className="text-3xl font-semibold text-primary-700 mb-2">Product Not Found</h2>
          <p className="text-primary-700 mb-6">The product you're looking for isn't available.</p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700 transition"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const discount = product.discountedPrice
    ? calculateDiscountPercentage(product.price, product.discountedPrice)
    : 0;

  const handleAddToCart = () => {
    if (product.stock === 0) return toast.error('Out of stock');
    addToCart(product.id, quantity);
  };

  const handleBuyNow = () => {
    if (product.stock === 0) return toast.error('Out of stock');
    addToCart(product.id, quantity);
    navigate('/cart');
  };

  const productTypeName = isBook(product) ? 'Book' : 'Stationery';

  return (
    <>
      <Helmet>
        <title>
          {product.title}
          {isBook(product) && product.author ? ` by ${product.author}` : ''} | Bookstore
        </title>
        <meta name="description" content={product.description} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container-custom py-12">
          <Link
            to={'/products'}
            className="inline-flex items-center text-primary-700 hover:text-primary-800 mb-8 group"
            aria-label="Back to products"
          >
            <ChevronLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to {productTypeName}s</span>
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start mb-16">
            {/* Left - Images */}
            <div className="space-y-6">
              <div className="aspect-[3.5/4] rounded-3xl overflow-hidden bg-white border border-[rgba(45,45,45,0.06)] relative">
               
              

                <>
            {/* Blurred background image */}
            <img
             src={product.images[selectedImage] || product.images[0] || Placeholder}
             alt={product.title.slice(0, 50) + (product.title.length > 50 ? '...' : '')}
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover blur-sm scale-110 opacity-40"
              loading="lazy"
            />
            {/* Main image - object-contain */}
            <img
             src={product.images[selectedImage] || product.images[0] || Placeholder}
              alt={product.title.slice(0, 50) + (product.title.length > 50 ? '...' : '')}
              onLoad={() => setImageLoaded(true)}
              className={`relative z-0 w-full h-full object-contain p-0 transition-all duration-500 ${
                imageLoaded 
                  ? 'opacity-100 transition-transform duration-600 transform-gpu hover:scale-105 ' 
                  : 'opacity-0'
              }`}
              loading="lazy"
            />
          </>

                {/* Arrows */}
                {product.images.length > 1 && (
                  <>
                    <button
                      onClick={() =>
                        setSelectedImage((p) => (p === 0 ? product.images.length - 1 : p - 1))
                      }
                      aria-label="Previous image"
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-background rounded-full flex items-center justify-center hover:scale-105 transition border border-[rgba(45,45,45,0.06)]"
                    >
                      <ChevronLeft className="w-5 h-5 text-primary-700" />
                    </button>
                    <button
                      onClick={() =>
                        setSelectedImage((p) => (p === product.images.length - 1 ? 0 : p + 1))
                      }
                      aria-label="Next image"
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-background rounded-full flex items-center justify-center hover:scale-105 transition border border-[rgba(45,45,45,0.06)]"
                    >
                      <ChevronLeft className="w-5 h-5 text-primary-700 rotate-180" />
                    </button>
                  </>
                )}

                {discount > 0 && (
                  <div className="absolute top-5 left-5">
                    <div className="bg-primary-100 text-primary-700 text-sm font-semibold px-3 py-1.5 rounded-full border border-[rgba(45,45,45,0.04)]">
                      {discount}% OFF
                    </div>
                  </div>
                )}

                {product.stock === 0 && (
                  <div className="absolute inset-0 bg-[rgba(45,45,45,0.35)] flex items-center justify-center">
                    <div className="bg-background px-6 py-2 rounded-xl font-semibold text-primary-700 border border-[rgba(45,45,45,0.06)]">
                      Out of Stock
                    </div>
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-3">
                 {/* mock some photos for testing  */}
                   {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      aria-label={`Select image ${idx + 1}`}
                      className={`aspect-[3/4] rounded-xl overflow-hidden border ${
                        selectedImage === idx
                          ? 'ring-2 ring-primary-200 scale-100'
                          : 'border-[rgba(45,45,45,0.04)] hover:opacity-90'
                      } transition`}
                    >
                      <img src={img || Placeholder} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right - Info */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <span className="text-sm px-3 py-1 rounded-full bg-background-soft text-primary-700 rounded-lg">
                  {product.productType === ProductType.BOOK ? 'Book' : 'Stationery'}
                </span>
                {product.isFeatured && (
                  <span className="text-sm px-3 py-1 rounded-md bg-primary-100 text-primary-700 border border-[rgba(45,45,45,0.04)]">
                    Featured
                  </span>
                )}
              </div>

              <div>
                <h1 className="text-4xl font-extrabold leading-tight text-primary-900">
                  {product.title}
                </h1>
                {isBook(product) && product.author && (
                  <p className="text-lg text-primary-700 mt-2">
                    by <span className="font-medium text-primary-800">{product.author}</span>
                  </p>
                )}

                {!isBook(product) && (product as any).brand && (
                  <p className="text-lg text-primary-700 mt-2">
                    Brand: <span className="font-medium text-primary-800">{(product as any).brand}</span>
                  </p>
                )}
              </div>

              {/* Rating */}
              <div className="flex items-center gap-4 border-b border-[rgba(45,45,45,0.06)] pb-6">
                <StarRating rating={product.rating} size={20} />
                <div className="text-sm font-semibold text-primary-800">{product.rating.toFixed(1)}</div>
                <div className="text-sm text-primary-700">· {product.reviewCount} reviews</div>
              </div>

              {/* Price */}
              <div className="flex items-end gap-4 border-b border-[rgba(45,45,45,0.06)] pb-6">
                <div>
                  <div className="text-4xl lg:text-5xl font-extrabold text-primary-900">
                    {formatPrice(product.discountedPrice || product.price)}
                  </div>
                  {product.discountedPrice && product.discountedPrice < product.price && (
                    <div className="mt-1 flex items-center gap-3">
                      <div className="text-sm text-primary-700 line-through">{formatPrice(product.price)}</div>
                      <div className="text-sm font-medium text-primary-600">
                        Save {formatPrice(product.price - product.discountedPrice)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <div
                  className="text-primary-700 text-lg leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              </div>

              {/* Book details */}
              {isBook(product) ? (
                <div className="bg-background-soft/70 rounded-2xl p-5 border border-[rgba(45,45,45,0.04)]">
                  <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-primary-700">
                    {product.publisher && (
                      <>
                        <div className="text-primary-700/75">Publisher</div>
                        <div className="font-medium text-primary-900">{product.publisher}</div>
                      </>
                    )}
                    {product.language && (
                      <>
                        <div className="text-primary-700/75">Language</div>
                        <div className="font-medium text-primary-900">{product.language}</div>
                      </>
                    )}
                    {product.pages && (
                      <>
                        <div className="text-primary-700/75">Pages</div>
                        <div className="font-medium text-primary-900">{product.pages}</div>
                      </>
                    )}
                    {product.isbn && (
                      <>
                        <div className="text-primary-700/75">ISBN</div>
                        <div className="font-medium text-primary-900">{product.isbn}</div>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                (product as any).attributes && (
                  <div className="bg-background-soft/70 rounded-2xl p-5 border border-[rgba(45,45,45,0.04)]">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-primary-700">
                      {Object.entries((product as any).attributes || {}).map(([key, value]) => (
                        <React.Fragment key={key}>
                          <div className="text-primary-700/75 capitalize">{key}</div>
                          <div className="font-medium text-primary-900">{value as string}</div>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                )
              )}

              {/* tags */}
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-sm px-3 py-1 rounded-md bg-background-soft/70 text-primary-800 border border-[rgba(45,45,45,0.04)] font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Availability & Quantity */}
              <div className="flex items-center gap-4">
                {/* <div className={`flex items-center gap-3 rounded-md px-3 py-2 ${product.stock > 0 ? 'bg-primary-100 text-primary-700' : 'bg-background-soft text-primary-700'}`}>
                  {
                    product.stock > 0 ? (
                      <Check className="w-4 h-4 text-primary-600" />
                    
                    ):
                    ( 
                      <X className="w-4 h-4 text-primary-900" />
                    ) 
                  }
                  <div className={`text-sm font-medium`}>
                    {product.stock > 0 ? `In stock · ${product.stock} available` : 'Out of stock'}
                  </div>
                </div> */}

                {product.stock > 0 && (
                  <div className="ml-auto inline-flex items-center bg-primary-100 rounded-md overflow-hidden border border-[rgba(45,45,45,0.04)]">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-4 py-2 hover:bg-background-soft transition"
                      aria-label="Decrease quantity"
                    >
                      <Minus className="w-4 h-4 text-primary-700" />
                    </button>
                    <div className="px-6 py-2 font-medium text-primary-900">{quantity}</div>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      className="px-4 py-2 hover:bg-background-soft transition"
                      aria-label="Increase quantity"
                    >
                      <Plus className="w-4 h-4 text-primary-700" />
                    </button>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleBuyNow}
                  disabled={product.stock === 0}
                  className={`flex-1 py-4 rounded-md font-semibold text-lg flex items-center justify-center gap-3 transition ${
                    product.stock === 0
                      ? 'bg-background text-primary-700/40 cursor-not-allowed border border-[rgba(45,45,45,0.04)]'
                      : 'bg-primary-600 text-white hover:bg-primary-700'
                  }`}
                >
                  <ShoppingCart className="w-5 h-5" />
                  Buy Now
                </button>

                <button
                  onClick={handleAddToCart}
                  disabled={product.stock === 0}
                  className={`flex-1 py-4 rounded-md font-semibold text-lg flex items-center justify-center gap-3 transition border ${
                    product.stock === 0
                      ? 'bg-background text-primary-700/40 cursor-not-allowed border-[rgba(45,45,45,0.04)]'
                      : inCart
                      ? 'bg-primary-100 text-primary-700 border border-primary-100 hover:bg-primary hover:text-white transition'
                      : 'bg-background-soft text-primary-900 border border-[rgba(45,45,45,0.06)] hover:border-primary-200'
                  }`}
                >
                  {inCart ? (
                    <>
                      <Plus className="w-5 h-5" />
                      In Cart ({cartQuantity})
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="w-5 h-5" />
                      Add to Cart
                    </>
                  )}
                </button>

                <button
                  onClick={() => toggleWishlist(product.id)}
                  className={`p-3 rounded-xl border-2 transition ${
                    inWishlist ? 'bg-primary-100 border-primary-200' : 'border-[rgba(45,45,45,0.06)] bg-background'
                  }`}
                  aria-label="Toggle wishlist"
                >
                  <Heart
                    className={`w-5 h-5 ${
                      inWishlist ? 'text-primary-700 fill-primary-600' : 'text-primary-700/60'
                    }`}
                  />
                </button>
              </div>

              {/* Feature chips */}
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-primary-200">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2 bg-primary-100 border border-primary-200">
                    <Truck className="w-5 h-5 text-primary-700" />
                  </div>
                  <p className="text-sm font-medium text-primary-900">Free Shipping</p>
                  <p className="text-xs text-primary-700">Above ₹500</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2 bg-primary-100 border border-primary-200">
                    <Shield className="w-5 h-5 text-primary-700" />
                  </div>
                  <p className="text-sm font-medium text-primary-900">Secure Payment</p>
                  <p className="text-xs text-primary-700">Safe & encrypted</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-2 bg-primary-100 border border-primary-200">
                    <Package className="w-5 h-5 text-primary-700" />
                  </div>
                  <p className="text-sm font-medium text-primary-900">Easy Returns</p>
                  <p className="text-xs text-primary-700">7-day policy</p>
                </div>
              </div>
            </div>
          </div>

          {/* Reviews */}
          <div className="mb-16 " id="reviews">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-primary-900">Customer Reviews</h2>
                <p className="text-primary-700 mt-1">What readers say</p>
              </div>
              {currentUserId && (
                <button
                  onClick={() => setShowReviewForm(true)}
                  className="px-5 py-3 bg-primary-600 text-white rounded-xl flex items-center gap-2 hover:bg-primary-700 transition"
                >
                  {userReview ? <Edit className="w-4 h-4" /> : <MessageSquare className="w-4 h-4" />}
                  {userReview ? 'Edit Review' : 'Write Review'}
                </button>
              )}
            </div>

            {/* Ratings distribution */}
            {reviews && reviews.length > 0 ? (
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-11 pb-11 border-b border-[rgba(45,45,45,0.06)] max-xl:max-w-2xl max-xl:mx-auto bg-white/50 rounded-2xl p-6 mb-10">
                <div className="box flex flex-col gap-y-4 w-full">
                  {[5, 4, 3, 2, 1].map((rating) => {
                    const count = reviews.filter((r) => r.rating === rating).length;
                    const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;

                    return (
                      <div key={rating} className="flex items-center w-full">
                        <p className="font-medium text-lg text-primary-900 mr-0.5">{rating}</p>
                        <StarRating rating={rating} size={18} readOnly />
                        <div className="h-2 flex-1  rounded-3xl bg-background ml-5 mr-3 overflow-hidden">
                          <div
                            className="h-full rounded-3xl bg-primary-600 flex transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <p className="font-medium text-lg text-primary-900">{count.toLocaleString()}</p>
                      </div>
                    );
                  })}
                </div>

                <div className="p-8 bg-primary-100/50 rounded-3xl flex items-center justify-center flex-col">
                  <h2 className="font-bold text-5xl text-primary-600 mb-6">
                    {product.rating.toFixed(1)}
                  </h2>
                  <div className="flex items-center justify-center gap-2 sm:gap-6 mb-4">
                    <StarRating rating={product.rating} size={40} readOnly />
                  </div>
                  <p className="font-medium text-xl leading-8 text-primary-900 text-center">
                    {reviews.length.toLocaleString()} {reviews.length === 1 ? 'Rating' : 'Ratings'}
                  </p>
                </div>
              </div>
            ) : null}

            {reviewsLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin h-10 w-10 border-b-4 border-primary-600 rounded-full" />
              </div>
            ) : reviews?.length === 0 ? (
              <div className="text-center py-12 bg-background rounded-2xl border border-[rgba(45,45,45,0.04)]">
                <MessageSquare className="w-12 h-12 text-primary-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-primary-900">No reviews yet</h3>
                <p className="text-primary-700 mt-2">Be the first to share your thoughts.</p>
              </div>
            ) : (
              <PaginatedReviews reviews={reviews} currentUserId={currentUserId} />
            )}
          </div>

          {/* Related */}
          {relatedProducts && relatedProducts.length > 0 && (
            <div>
              <h3 className="text-xl font-semibold text-primary-900 mb-4">You may also like</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {relatedProducts.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={showReviewForm} onClose={() => setShowReviewForm(false)} title={userReview ? 'Edit Your Review' : 'Write a Review'}>
        <ReviewForm
          productId={product?.id || ''}
          productTitle={product?.title || ''}
          existingReview={userReview}
          onSuccess={() => {
            setShowReviewForm(false);
            toast.success(userReview ? 'Review updated!' : 'Review posted!');
          }}
          onCancel={() => setShowReviewForm(false)}
        />
      </Modal>
    </>
  );
};

export default ProductDetailPage;

/**
 * Helper components
 */

/**
 * Star (full / half / empty) — uses inline SVG so we can precisely control
 * half-star rendering (option B: half-star support requested).
 */
const StarIcon = ({ variant = 'full', size = 20 }: { variant?: 'full' | 'half' | 'empty'; size?: number }) => {
  const fill = '#A8523A'; // primary DEFAULT (terracotta)
  const stroke = 'rgba(45,45,45,0.12)';
  // Full star (filled)
  if (variant === 'full') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.788 1.402 8.174L12 18.896l-7.336 3.277 1.402-8.174L.132 9.211l8.2-1.193L12 .587z" />
      </svg>
    );
  }

  // Half star
  if (variant === 'half') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <defs>
          <linearGradient id="halfGrad">
            <stop offset="50%" stopColor={fill} />
            <stop offset="50%" stopColor="transparent" stopOpacity="1" />
          </linearGradient>
        </defs>
        <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.788 1.402 8.174L12 18.896l-7.336 3.277 1.402-8.174L.132 9.211l8.2-1.193L12 .587z" fill="url(#halfGrad)" stroke={stroke} />
      </svg>
    );
  }

  // Empty star (outline subtle)
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="rgba(45,45,45,0.18)" strokeWidth="1.2" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.788 1.402 8.174L12 18.896l-7.336 3.277 1.402-8.174L.132 9.211l8.2-1.193L12 .587z" />
    </svg>
  );
};

/**
 * StarRating — render n full / half / empty stars for a decimal rating (0-5)
 * - size: pixel size of each star
 * - readOnly: if true, buttons/ARIA not included
 */
const StarRating = ({ rating = 0, size = 20, readOnly = false }: { rating?: number; size?: number; readOnly?: boolean }) => {
  // Determine counts
  const fullCount = Math.floor(rating);
  const hasHalf = rating - fullCount >= 0.25 && rating - fullCount < 0.75;
  const roundUp = rating - fullCount >= 0.75;
  const display = [];

  if (roundUp) {
    // e.g. 4.8 => 5 full
    for (let i = 0; i < 5; i++) display.push('full');
  } else {
    for (let i = 0; i < fullCount; i++) display.push('full');
    if (hasHalf) display.push('half');
    while (display.length < 5) display.push('empty');
  }

  return (
    <div className={`flex items-center gap-1 ${readOnly ? '' : 'aria-hidden'}`} role={readOnly ? 'img' : undefined} aria-label={`Rating ${rating} out of 5`}>
      {display.map((v, i) => (
        <span key={i} className="inline-block" style={{ lineHeight: 0 }}>
          <StarIcon variant={v as any} size={size} />
        </span>
      ))}
    </div>
  );
};

/**
 * PaginatedReviews & ExpandableComment remain functionally same but styled to match palette.
 */
const PaginatedReviews = ({ reviews, currentUserId }: { reviews: any[]; currentUserId?: string }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 6;
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);
  const startIdx = (currentPage - 1) * reviewsPerPage;

  const mockReviews = [...reviews.slice(startIdx, startIdx + reviewsPerPage)];
  const currentReviews = mockReviews.slice(0, reviewsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const reviewsSection = document.getElementById('reviews');
    if (reviewsSection) {
      reviewsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <div className="p-4 bg-background-soft rounded-2xl space-y-4 transition-all duration-300 ease-in-out">
        {currentReviews.map((review) => (
          <div
            key={review.id}
            className="bg-background rounded-2xl p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary-300/20 flex items-center justify-center text-primary-700 font-semibold">
                {review.userName?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-primary-900">{review.userName}</h4>
                      {review.userId === currentUserId && (
                        <span className="text-xs px-2 py-0.5 bg-background rounded-full text-primary-700">You</span>
                      )}
                    </div>
                    <div className="text-xs text-primary-700 mt-1">
                      {format(review.createdAt.toDate(), 'MMM dd, yyyy')}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex items-center gap-1">
                    <StarRating rating={review.rating} size={16} readOnly />
                  </div>
                </div>
                <ExpandableComment text={review.comment} />
              </div>
            </div>
          </div>
        ))}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </>
  );
};

const ExpandableComment = ({ text }: { text: string }) => {
  const [expanded, setExpanded] = useState(false);
  const maxChars = 300;
  const isLong = text.length > maxChars;
  const displayText = expanded ? text : text.slice(0, maxChars);

  return (
    <p className="text-primary-700 leading-relaxed">
      {displayText}
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-primary-600 font-medium ml-2 hover:underline"
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </p>
  );
};
